import { useTheme } from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { Layout, PlotData } from "plotly.js";
import { useMemo } from "react";
import Plot from "components/plotly/LocalisedPlot";
import { defaultLayout, setSelectedDateFromPlotClick } from "../plotting";
import { ScrapChefUsageDetailedPoint } from "src/store/api/generatedApi";
import { useFormatNumber } from "src/utils/formatNumber";

interface ScrapChefUsageDetailedPlotProps {
  data: ScrapChefUsageDetailedPoint[];
  setSelectedDate: (date: Date) => void;
}

export const ScrapChefUsageDetailedPlot = ({
  data,
  setSelectedDate,
}: ScrapChefUsageDetailedPlotProps) => {
  const { t } = useTenantTranslation();
  const formatNumber = useFormatNumber();
  const usageBlue = useTheme().palette.data.blue;

  const formatPercent = formatNumber(1);

  const plotData = useMemo(
    () =>
      buildFigData(data, t, usageBlue, (value) => `${formatPercent(value)}%`),
    [data, t, usageBlue, formatPercent]
  );
  const layout = useMemo(() => buildLayout(t), [t]);

  return (
    <div style={{ width: "100%" }}>
      <Plot
        data={plotData}
        layout={layout}
        style={{ width: "100%" }}
        useResizeHandler
        onClick={setSelectedDateFromPlotClick(setSelectedDate)}
      />
    </div>
  );
};

const buildFigData = (
  data: ScrapChefUsageDetailedPoint[],
  t: (key: string) => string,
  usageBlue: string,
  formatLabel: (value: number) => string
): Partial<PlotData>[] => {
  const allData = data.filter((d) => d.standard === "all");
  const dates = allData.map((d) => d.period_start);
  const values = allData.map((d) => d.scrap_chef_percentage);

  return [
    {
      x: dates,
      y: values,
      type: "bar",
      name: t("scrapChefUsage"),
      marker: { color: usageBlue },
      text: values.map((value) => (value === null ? "" : formatLabel(value))),
      textposition: "outside",
      cliponaxis: false,
    },
  ];
};

const buildLayout = (t: (key: string) => string): Partial<Layout> => ({
  ...defaultLayout,
  height: 500,
  yaxis: {
    title: { text: `${t("scrapChefUsage")} (%)` },
    range: [0, 105],
    ticksuffix: "%",
  },
  xaxis: {
    title: { text: t("date") },
  },
});
